import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router'
import { api, ErroreApi } from '../../servizi/api.ts'
import { Attesa, Avviso, Dato, Intestazione } from '../../componenti/Pezzi.tsx'
import { Bottone, Etichetta, Riquadro } from '../../componenti/base.tsx'
import { IconaIndietro } from '../../componenti/Icone.tsx'
import { CompletaDati, sezioniDaCompletare } from './CompletaDati.tsx'
import { SegnaNonPresentata, SegnaSvolta } from './comandi.tsx'
import {
  STATI, durataLeggibile, giornoDiOggi, giornoLungo, giornoVicino, type DettaglioMediazione,
} from '../../servizi/mediazioni.ts'

/**
 * La mediazione vista dal mediatore: giorno, ora e luogo, la nota di chi l'ha programmata, il nome
 * della persona e i dati che le mancano. Da qui parte il percorso guidato per completarli e si
 * segna l'esito: svolta, con le ore, oppure «la persona non si è presentata» (457). I dati gia'
 * scritti nella scheda non si vedono.
 */
const maiuscola = (testo: string) => testo.charAt(0).toUpperCase() + testo.slice(1)

export function Mediazione() {
  const { id = '' } = useParams()
  const [m, setM] = useState<DettaglioMediazione | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [completa, setCompleta] = useState(false)
  const [svolta, setSvolta] = useState(false)
  const [assente, setAssente] = useState(false)
  const [esito, setEsito] = useState<string | null>(null)

  // Si rilegge senza svuotare la pagina: il percorso guidato resta aperto sopra.
  const carica = useCallback(() => {
    api.get<DettaglioMediazione>(`/mediazioni/mie/${id}`)
      .then(setM)
      .catch((e) => setErrore(e instanceof ErroreApi && e.stato === 404
        ? 'Questa mediazione non c’è, oppure non è più fra le tue.'
        : 'Non riesco a caricare la mediazione. Riprova fra poco.'))
  }, [id])
  useEffect(carica, [carica])

  if (errore) {
    return (
      <>
        <Avviso tipo="errore">{errore}</Avviso>
        <Link to="/mediazioni">Torna alle tue mediazioni</Link>
      </>
    )
  }
  if (!m) return <Attesa testo="Caricamento della mediazione…" />

  const nome = m.persona.nome
  const sezioni = sezioniDaCompletare(m.mancanti)
  const futura = m.data > giornoDiOggi()

  return (
    <>
      <Intestazione
        titolo={`Mediazione con ${m.persona.nome} ${m.persona.cognome}`}
        sottotitolo={<Link to="/mediazioni" className="titolo-con-icona"><IconaIndietro /> Le mie mediazioni</Link>}
        azioni={<>
          {/* Il file per il calendario del telefono (557): giorno, ora, lingua, sede; mai il nome. */}
          <a className="bottone bottone-secondario bottone-piccolo" href={`/api/mediazioni/${m.id}.ics`} download
             title="Aggiungi la mediazione al calendario del telefono o di Outlook">Aggiungi al calendario (.ics)</a>
          <Etichetta tono={STATI[m.stato].tono}>{STATI[m.stato].etichetta}</Etichetta>
        </>} />

      {esito && <Avviso tipo="successo">{esito}</Avviso>}

      <div className="mediazioni-testata">
        <span className={`mediazioni-quando${m.stato === 'programmata' ? '' : ' mediazioni-quando-passato'}`}>
          <span className="mediazioni-quando-giorno">{giornoVicino(m.data)}</span>
          <span className="mediazioni-quando-ora">{m.ora}</span>
        </span>
        <div className="mediazioni-testo">
          <span className="mediazioni-chi">{durataLeggibile(m.durata_minuti)}{m.lingua_nome && `, in ${m.lingua_nome.toLowerCase()}`}</span>
          <span className="mediazioni-dettagli">
            {[m.sede_nome, m.sede_indirizzo, m.luogo].filter(Boolean).join(' · ') || 'Il luogo te lo dice chi l’ha programmata.'}
          </span>
        </div>
      </div>

      {m.nota && (
        <Avviso tipo="informazione">
          <strong>Nota per te{m.programmata_da ? ` da ${m.programmata_da.nome} ${m.programmata_da.cognome}` : ''}:</strong>{' '}
          {m.nota}
        </Avviso>
      )}

      <Riquadro titolo={`I dati di ${nome}`}>
        {m.stato === 'annullata' && <p>La mediazione è stata annullata: non c’è niente da fare.</p>}
        {m.stato === 'non_presentata' && <p>La persona non si è presentata: non c’è niente da completare.</p>}
        {m.stato === 'svolta' && !m.completabile && (
          <p>La mediazione è chiusa da più di una settimana: ora i dati di {nome} li aggiorna l’operatore.</p>
        )}
        {m.completabile && m.puo_completare && sezioni.length > 0 && (
          <>
            <p>
              Nella scheda di {nome} {m.mancanti.length === 1 ? 'manca un dato' : `mancano ${m.mancanti.length} dati`}.
              Puoi chiederli durante l’incontro e inserirli tu: vedi solo i campi vuoti, quelli già scritti non si
              vedono e non si cambiano da qui.
            </p>
            <div className="riga">
              <Bottone variante="primario" onClick={() => { setEsito(null); setCompleta(true) }}>
                Completa i dati di {nome}
              </Bottone>
            </div>
          </>
        )}
        {m.completabile && m.puo_completare && sezioni.length === 0 && (
          <Avviso tipo="successo">La scheda di {nome} ha già tutti i dati che puoi inserire tu.</Avviso>
        )}
        {m.completabile && !m.puo_completare && (
          <p>I dati della persona li inserisce l’operatore che ha programmato la mediazione.</p>
        )}
      </Riquadro>

      <Riquadro titolo="L’incontro">
        <dl className="elenco-dati">
          <Dato nome="Giorno">{maiuscola(giornoLungo(m.data))}</Dato>
          <Dato nome="Ora d’inizio">{m.ora}</Dato>
          <Dato nome="Durata prevista">{durataLeggibile(m.durata_minuti)}</Dato>
          <Dato nome="Lingua">{m.lingua_nome}</Dato>
          <Dato nome="Sede">{m.sede_nome && [m.sede_nome, m.sede_indirizzo].filter(Boolean).join(', ')}</Dato>
          <Dato nome="Stanza">{m.stanza}</Dato>
          <Dato nome="Luogo">{m.luogo}</Dato>
          <Dato nome="Programmata da">{m.programmata_da && `${m.programmata_da.nome} ${m.programmata_da.cognome}`}</Dato>
          {m.stato === 'svolta' && m.ore_svolte != null && (
            <Dato nome="Durata effettiva">{durataLeggibile(m.ore_svolte * 60)}</Dato>
          )}
        </dl>
        {m.stato === 'programmata' && (
          <div className="riga">
            {m.puo_segnare_svolta && (
              <Bottone variante="secondario" onClick={() => { setEsito(null); setSvolta(true) }}>Segna come svolta</Bottone>
            )}
            {m.puo_segnare_non_presentata && (
              <Bottone variante="discreto" onClick={() => { setEsito(null); setAssente(true) }}>
                La persona non si è presentata
              </Bottone>
            )}
            {!m.puo_segnare_svolta && futura && (
              <p className="nota-modulo">Dal giorno dell’incontro potrai segnarla come svolta, oppure segnare che la persona non si è presentata.</p>
            )}
          </div>
        )}
      </Riquadro>

      {completa && (
        <CompletaDati mediazioneId={m.id} nome={nome} mancanti={m.mancanti}
                      chiudi={() => { setCompleta(false); carica() }} fatto={carica} />
      )}
      {svolta && (
        <SegnaSvolta mediazione={m} chiudi={() => setSvolta(false)}
                     fatto={() => { setSvolta(false); setEsito('Fatto: la mediazione risulta svolta. Grazie!'); carica() }} />
      )}
      {assente && (
        <SegnaNonPresentata mediazione={m} nome={nome} chiudi={() => setAssente(false)}
                            fatto={() => {
                              setAssente(false)
                              setEsito('Fatto. Chi ha programmato la mediazione riceve un avviso per fissarne un’altra.')
                              carica()
                            }} />
      )}
    </>
  )
}
